import merge from 'lodash/merge';

import {createSlice, PayloadAction, createSelector} from '@reduxjs/toolkit';
import type {
	OrdersState,
	ReceiveLoadingStatusPayload,
	ReceiveOrderPayload,
	ReceiveOrdersPayload,
} from './types';
import {MY_ACCOUNT_ORDERS_PER_PAGE} from 'brand/app/constants';
import {RootState} from '../../types';

const calcPageNumber = (offset) => {
	return (offset / MY_ACCOUNT_ORDERS_PER_PAGE) + 1 || 1;
};

const initialState: OrdersState = {};
const initialInstanceState = {
	isLoading: false,
	loaded: false,
	limit: null,
	total: null,
	offset: null,
	orders: {},
	pages: {},
};

export const ordersSlice = createSlice({
	name: 'orders',
	initialState,
	reducers: {
		receiveOrder: (state: OrdersState, action: PayloadAction<ReceiveOrderPayload>) => {
			const key = action.payload.key;
			const instanceState = Object.assign({},
				{
					isLoading: false,
					loaded: true,
					orders: {[action.payload.order.orderNo]: action.payload.order}
				}
			);
			const newState = {};

			newState[key] = instanceState;

			merge(state, newState);
		},
		receiveOrders: (state: OrdersState, action: PayloadAction<ReceiveOrdersPayload>) => {
			const key = action.payload.key;
			const pageNumber = calcPageNumber(action.payload.offset);
			const instanceState = Object.assign({},
				{
					isLoading: false,
					loaded: true,
					limit: action.payload.limit,
					total: action.payload.total,
					offset: action.payload.offset,
				}
			);
			const newState = {};

			newState[key] = instanceState;

			if (action.payload.data && action.payload.limit > 0) {
				Object.assign(newState[key], {
					pages: {
						[pageNumber]: action.payload.data.map((o) => o.orderNo)
					},
					orders: action.payload.data.reduce((a, o) => (
						{...a, [o.orderNo]: o}
					), {})
				});
			}

			merge(state, newState);
		},
		receiveLoadingStatus: (state: OrdersState, action: PayloadAction<ReceiveLoadingStatusPayload>) => {
			const key = action.payload.key;
			const instanceState = Object.assign({},
				{
					isLoading: action.payload.isLoading,
				}
			);
			const newState = {};

			newState[key] = instanceState;

			merge(state, newState);
		},
		cleanupOrders: (state: OrdersState) => {
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			Object.keys(state).forEach((key) => delete state[key]);
		},
	},
});

export const {
	receiveOrder,
	receiveOrders,
	receiveLoadingStatus,
	cleanupOrders,
	receiveTotalOrders
} = ordersSlice.actions as any;

const ordersStateSelector = (state: RootState) => state.orders;
const selectKey = (state, key) => key;
export const selectInstance = createSelector([ordersStateSelector, selectKey], (data, key) =>
	data[key] ? Object.assign({}, initialInstanceState, data[key]) : initialInstanceState
);

const selectOrderNo = (state, key, orderNo) => orderNo;
export const selectLoadingStatus = createSelector([selectInstance], (instance) => ({
	isLoading: instance.isLoading,
	loaded: instance.loaded,
}));
export const selectOrders = createSelector([selectInstance], (instance) => instance.orders);
export const selectTotalOrders = createSelector([selectInstance], (instance) => instance.total);
export const selectPages = createSelector([selectInstance], (instance) => {
	return Object.keys(instance.pages).reduce((acc, key) => {
		const page = instance.pages[key];

		const orders = page.map((orderNo) => instance.orders[orderNo]);

		return {...acc, [key]: orders};
	}, {});
});
export const selectPagination = createSelector([selectInstance], (instance) => ({
	total: instance.total,
	offset: instance.offset,
	limit: instance.limit
}));
export const selectOrder = createSelector([selectOrders, selectOrderNo], (orders, orderNo) => orders[orderNo]);

export default ordersSlice.reducer;
